import React, { useEffect, useState, useCallback } from 'react';
import { Modal, View, TouchableOpacity, Image } from 'react-native';
import closeIcon from 'src/assets/close.png';
import Button from 'src/components/Button/Button';
import Typography from 'src/components/Text/Text';
import { styles } from './CheckListModal.styles';
import CommoditySelector from 'src/components/CheckListModal/CheckListSelector/CheckListSelector';
import { CheckListModalList, Selection } from '@typeRoots/common/checkListModal';

interface ChecklistModalProps {
  isVisible: boolean;
  initialSelections: Selection[];
  onClose: () => void;
  title: string;
  handleSelectionChange: (newSelection: Selection[]) => void;
  list: CheckListModalList;
  parentTestID?: string;
  hasSearch?: boolean;
  searchPlaceholder?: string;
}

const ChecklistModal: React.FC<ChecklistModalProps> = ({
  isVisible,
  initialSelections,
  onClose,
  title,
  handleSelectionChange,
  list,
  parentTestID,
  hasSearch = false,
  searchPlaceholder,
}) => {
  const [selectedCommodities, setSelectedCommodities] = useState<Selection[]>([]);

  // Reset the state only when the modal is visible and initial selections change
  useEffect(() => {
    if (isVisible) {
      setSelectedCommodities(initialSelections); // Load initial selections when modal is visible
    }
  }, [isVisible, initialSelections]);

  // Use useCallback to memoize the save handler to avoid unnecessary re-renders
  const handleSave = useCallback(() => {
    handleSelectionChange(selectedCommodities); // Save selected commodities
    onClose(); // Close modal
  }, [selectedCommodities, handleSelectionChange, onClose]);

  return (
    <Modal visible={isVisible} transparent animationType="slide" testID={parentTestID}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Typography variant="h2">{title}</Typography>
            <TouchableOpacity onPress={onClose} style={styles.closeIcon}>
              <Image source={closeIcon} style={styles.closeImage} />
            </TouchableOpacity>
          </View>

          {/* Commodity Selector */}
          <CommoditySelector
            onSelectionChange={setSelectedCommodities}
            initialSelection={selectedCommodities} // Ensure controlled state
            list={list}
            parentTestID={parentTestID}
            shouldResetSearch={!isVisible}
            hasSearch={hasSearch}
            searchPlaceholder={searchPlaceholder}
          />

          <View style={styles.buttonContainer}>
            <View style={styles.buttonWrapper1}>
              <Button
                type="tertiary"
                title="Back"
                onPress={onClose}
                testID={`${parentTestID}-back-btn`}
              />
            </View>
            <View style={styles.buttonWrapper2}>
              <Button
                type="primary"
                title="Save"
                onPress={handleSave}
                testID={`${parentTestID}-save-btn`}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ChecklistModal;
